export const productionEnvironmentNames = [
  'production',
  'live', // please use production instead of live
] as const;
export const stagingEnvironmentNames = ['staging'] as const;
export const testEnvironmentNames = ['test'] as const;
export const teamEnvironmentNames = ['team-a', 'team-b', 'team-c', 'team-d', 'team-e'] as const;
export const devEnvironmentNames = ['dev'] as const;
export const localEnvironmentNames = ['local'] as const;

export const liveEnvironmentNames = [...productionEnvironmentNames, ...stagingEnvironmentNames] as const;
export const environmentNames = [
  ...liveEnvironmentNames,
  ...testEnvironmentNames,
  ...teamEnvironmentNames,
  ...devEnvironmentNames,
  ...localEnvironmentNames,
] as const;

export type EnvironmentName = (typeof environmentNames)[number];

export const webAppNames = [
  'teacher',
  'student',
  'admin',
  'api',
  'search',
  'site',
  'graphqlPublicApi',
  'graphqlInternalApi',
  'graphqlInternalApiPrivateNetwork',
  'hocuspocus',
  'snowplow',
  'teacherNext',
] as const;

export const appNames = [...webAppNames, 'workers', 'api-media', 'thumbnailer'] as const;

export type WebAppName = (typeof webAppNames)[number];

export type AppName = (typeof appNames)[number];

export type WebAppToUrlMap = {
  [K in WebAppName]: string;
};

export const projectIds = ['lessonup-live', 'lessonup-test', 'lessonup-data', 'lessonup-devops'] as const;

export type ProjectId = (typeof projectIds)[number];

type EnvironmentToProjectIdMap = {
  [K in EnvironmentName]: ProjectId;
};

export const environmentToProjectMap: EnvironmentToProjectIdMap = {
  production: 'lessonup-live',
  live: 'lessonup-live',
  staging: 'lessonup-live',
  test: 'lessonup-test',
  dev: 'lessonup-test',
  'team-a': 'lessonup-test',
  'team-b': 'lessonup-test',
  'team-c': 'lessonup-test',
  'team-d': 'lessonup-test',
  'team-e': 'lessonup-test',
  local: 'lessonup-test',
};

type EnvironmentToSecretPrefixMap = {
  [K in EnvironmentName]: string;
};

export const environmentToSecretPrefixMap: EnvironmentToSecretPrefixMap = {
  production: 'lu-prod',
  live: 'lu-prod',
  staging: 'lu-prod',
  test: 'lu-test',
  dev: 'lessonup-dev',
  'team-a': 'team-a',
  'team-b': 'team-b',
  'team-c': 'team-c',
  'team-d': 'team-d',
  'team-e': 'team-e',
  local: 'lessonup-dev',
};

/**
 * returns urls for the specified environment
 * note that local, staging and production follow a different pattern than our development and test environments
 */
export const environmentToAppUrlMap = (environment: EnvironmentName): WebAppToUrlMap => {
  switch (environment) {
    case 'production':
    case 'live':
      return {
        teacher: 'https://www.lessonup.com/',
        teacherNext: 'https://www.lessonup.com/',
        admin: 'https://admin.lessonup.com/',
        student: 'https://lessonUp.app/',
        api: 'https://api.lessonup.com/v1/',
        search: 'https://www.lessonup.com/',
        site: 'https://www.lessonup.com/',
        graphqlPublicApi: 'https://graphql-api.lessonup.com',
        graphqlInternalApi: 'https://graphql-api-internal.lessonup.com',
        graphqlInternalApiPrivateNetwork: 'http://graphql-api-internal',
        hocuspocus: 'wss://hocuspocus.lessonup.com',
        snowplow: 'https://collector.lessonup.com',
      };
    case 'staging':
      return {
        teacher: 'https://staging.lessonup.com/',
        teacherNext: 'https://staging.lessonup.com/',
        admin: 'https://admin-staging.lessonup.com/',
        student: 'https://staging.lessonup.app/',
        api: 'https://api.lessonup.com/v1/',
        search: 'https://staging.lessonup.com/',
        site: 'https://staging.lessonup.com/',
        graphqlPublicApi: 'https://graphql-api-staging.lessonup.com',
        graphqlInternalApi: 'https://graphql-api-internal-staging.lessonup.com',
        graphqlInternalApiPrivateNetwork: 'http://graphql-api-internal-staging',
        hocuspocus: 'wss://hocuspocus-staging.lessonup.com',
        snowplow: 'https://collector.lessonup.com',
      };
    case 'local':
      return {
        teacher: 'http://localhost:3000/',
        teacherNext: 'http://localhost:3200/',
        admin: 'http://localhost:3001/',
        student: 'http://localhost:3002/',
        api: 'http://localhost:3004/v1/',
        search: 'http://localhost:3100/',
        site: 'http://localhost:8080/',
        graphqlPublicApi: 'http://localhost:4000',
        graphqlInternalApi: 'http://localhost:4001',
        graphqlInternalApiPrivateNetwork: 'http://localhost:4001',
        hocuspocus: 'ws://localhost:4321',
        snowplow: 'https://collector.test.lessonup.dev',
      };
    case 'test':
    case 'dev':
    case 'team-a':
    case 'team-b':
    case 'team-c':
    case 'team-d':
    case 'team-e':
      return {
        teacher: `https://${environment}.lessonup.dev/`,
        teacherNext: `https://teacher-next.${environment}.lessonup.dev/`,
        admin: `https://admin.${environment}.lessonup.dev/`,
        student: `https://student.${environment}.lessonup.dev/`,
        api: `https://api.${environment}.lessonup.dev/v1/`,
        search: `https://${environment}.lessonup.dev/`,
        site: `https://${environment}.lessonup.dev/`,
        graphqlPublicApi: `https://graphql-api.${environment}.lessonup.dev`,
        graphqlInternalApi: `https://graphql-api-internal.${environment}.lessonup.dev`,
        graphqlInternalApiPrivateNetwork: 'http://graphql-api-internal',
        hocuspocus: `wss://hocuspocus.${environment}.lessonup.dev`,
        snowplow: 'https://collector.test.lessonup.dev',
      };
    default:
      throw new Error(`Unknown environment: ${environment}`);
  }
};
